import type { InboxItem, Lookups, Me, OrderScanStatus, WorkdaySyncRun } from '@ft/shared';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Chip, Panel, useCollapse } from '../lib/collapse';
import { useI18n } from '../i18n';
import { fetchFile, openFile, upload } from '../lib/download';
import { dfmt, dtfmt, eur2 } from '../lib/format';
import { api } from '../lib/http';
import { OrderForm, type PdfPrefill } from './OrderForm';
import { useLoad } from './ui';

type UploadResult = { state: 'new' | 'duplicate' | 'known'; ocr?: 'pending' | 'done' | 'failed' | null };

/**
 * Bestellordner-Abgleich und Posteingang „Gefundene Bestellungen“ auf der Seite Bestellungen.
 * PDFs kommen aus dem Ordner-Scan (tools/order-scan) oder werden hier hochgeladen (mehrere auf einmal).
 * Jede Person sieht standardmäßig nur ihre Funde (hochgeladen oder PM/Vertretung des Projekts) und prüft sie nacheinander.
 */
export function OrderInbox({ me, canEdit, lookups, onCreated, onChanged, say }: { me: Me; canEdit: boolean; lookups: Lookups | null; onCreated: (id: string) => void; onChanged?: () => void; say: (m: string) => void }) {
  const { t } = useI18n();
  const { data: scan, reload: reloadScan } = useLoad<OrderScanStatus>('/api/orders/scan');
  const { data: items, reload: reloadItems } = useLoad<InboxItem[]>('/api/orders/inbox');
  const [busy, setBusy] = useState<string | null>(null);
  const [form, setForm] = useState<{ item: InboxItem; pdf: PdfPrefill } | null>(null);
  const [open, setOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  // Warteschlange „nacheinander prüfen“: IDs der Funde, aktuelle Position
  const [queue, setQueue] = useState<{ ids: string[]; pos: number } | null>(null);
  const [upOpen, setUpOpen] = useState(false);
  const myProjects = (lookups?.projects ?? []).filter((p) => p.pm?.id === me.id || p.deputy?.id === me.id);
  const [upProject, setUpProject] = useState('');
  const [upState, setUpState] = useState<{ i: number; n: number } | null>(null);
  const [upSummary, setUpSummary] = useState<string[] | null>(null);
  const [dragging, setDragging] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  useEffect(() => { if (!upProject && myProjects.length === 1) setUpProject(myProjects[0]!.id); }, [upProject, myProjects]);
  // Link aus „Offene Punkte“ (#gefunden): zum Posteingang springen
  const { setOpen: setPanelOpen } = useCollapse();
  useEffect(() => { if (items?.length && location.hash === '#gefunden') { setPanelOpen(['orders.inbox'], true); boxRef.current?.scrollIntoView({ block: 'start' }); } }, [items]); // eslint-disable-line react-hooks/exhaustive-deps

  const pendingId = scan?.status.pending?.id;
  // Texterkennung läuft im Hintergrund: Liste regelmäßig nachladen, bis alle Funde gelesen sind
  const ocrPending = (items ?? []).some((i) => i.ocr === 'pending');
  useEffect(() => {
    if (!ocrPending) return;
    const h = setInterval(reloadItems, 15_000);
    return () => clearInterval(h);
  }, [ocrPending, reloadItems]);
  useEffect(() => {
    if (!pendingId) return;
    const h = setInterval(() => { reloadScan(); reloadItems(); }, 60_000);
    return () => clearInterval(h);
  }, [pendingId, reloadScan, reloadItems]);

  async function act(id: string, fn: () => Promise<unknown>, msg?: string) {
    setBusy(id);
    try { await fn(); reloadItems(); reloadScan(); if (msg) say(msg); } catch (e) { say((e as Error).message); } finally { setBusy(null); }
  }

  async function review(item: InboxItem) {
    setBusy(item.id);
    try {
      const file = await fetchFile(`/api/orders/inbox/${item.id}/pdf`, item.fileName);
      const suggestion = item.suggestion ?? { orderNumber: null, orderDate: null, serviceFrom: null, serviceTo: null, customerId: item.customer.id, totalCents: null, positions: [{ text: '', amountCents: 0, projectId: item.project.id }], warnings: ['orderNumber', 'orderDate', 'positions'] };
      setForm({ item, pdf: { file, result: { suggestion, existing: null, preview: [] } } });
    } catch (e) { say((e as Error).message); } finally { setBusy(null); }
  }

  const all = items ?? [];
  const mine = all.filter((i) => i.mine);
  const list = showAll ? all : mine;
  // In die Warteschlange: eigene, neue, fertig gelesene Funde – die ältesten zuerst (Reihenfolge des Hochladens)
  const queueable = mine.filter((i) => i.status === 'new' && i.ocr !== 'pending' && i.canReview).slice().reverse();

  function openAt(ids: string[], pos: number) {
    const next = ids.slice(pos).map((id) => all.find((i) => i.id === id)).find((i): i is InboxItem => !!i);
    if (!next) { setQueue(null); setForm(null); say(t('queue.done')); return; }
    const at = ids.indexOf(next.id);
    setQueue({ ids, pos: at });
    void review(next);
  }
  function startQueue() { if (queueable.length) openAt(queueable.map((i) => i.id), 0); }
  /** Nächste PDF der Warteschlange öffnen (nach Speichern, Verwerfen oder Überspringen). */
  function advance() {
    if (!queue) { setForm(null); return; }
    const pos = queue.pos + 1;
    if (pos >= queue.ids.length) { setQueue(null); setForm(null); reloadItems(); say(t('queue.done')); return; }
    openAt(queue.ids, pos);
  }

  async function uploadFiles(files: File[]) {
    if (!files.length) return;
    if (!upProject) { say(t('up.needProject')); return; }
    const pdfs = files.filter((f) => f.type === 'application/pdf' || /\.pdf$/i.test(f.name));
    const out: string[] = files.filter((f) => !pdfs.includes(f)).map((f) => t('up.notPdf', { file: f.name }));
    let done = 0, known = 0, reading = 0, failed = 0;
    for (let i = 0; i < pdfs.length; i++) {
      setUpState({ i: i + 1, n: pdfs.length });
      try {
        const r = await upload<UploadResult>('POST', '/api/orders/inbox', pdfs[i]!, { projectId: upProject, source: 'upload', path: pdfs[i]!.name });
        if (r.state === 'known') known++; else { done++; if (r.ocr === 'pending') reading++; }
      } catch (e) { failed++; out.push(`${pdfs[i]!.name}: ${(e as Error).message}`); }
    }
    setUpState(null);
    setUpSummary([
      [done ? t('up.done', { n: done }) : null, known ? t('up.known', { n: known }) : null, reading ? t('up.reading', { n: reading }) : null, failed ? t('up.failed', { n: failed }) : null].filter(Boolean).join(' · '),
      ...out,
    ].filter(Boolean));
    reloadItems(); reloadScan();
  }

  if (!scan) return null;
  const last = scan.status.last;

  return (
    <>
      <div className="box wdsync slim">
        <div className="wdsync-row">
          <div className="wdsync-main">
            <div className="lbl">{t('scan.title')}</div>
            {!scan.folders.length ? <div className="muted">{t('scan.noFolders')}</div>
              : last ? <ScanRun run={last} /> : <div className="muted">{t('scan.never', { n: scan.folders.length })}</div>}
            {scan.status.pending && <div className="wdsync-pending" role="status">{t('scan.pending', { by: scan.status.pending.requestedBy ?? '–', at: dtfmt(scan.status.pending.requestedAt!) })}</div>}
          </div>
          <span className="spacer" />
          {scan.folders.length > 0 && <button className="btn ghost" aria-expanded={open} onClick={() => setOpen((o) => !o)}>{open ? t('sync.hide') : t('scan.folders', { n: scan.folders.length })}</button>}
          {canEdit && scan.folders.length > 0 && <button className="btn" onClick={() => act('scan', () => api('POST', '/api/orders/scan/request'))} disabled={busy === 'scan' || !!scan.status.pending}>{scan.status.pending ? t('sync.requested') : t('scan.now')}</button>}
          {canEdit && <button className="btn" aria-expanded={upOpen} onClick={() => setUpOpen((o) => !o)} disabled={!lookups}>{t('up.button')}</button>}
        </div>
        {upOpen && lookups && (
          <div className="wdsync-detail upload">
            <div className="fld">
              <label htmlFor="up-p">{t('up.project')}</label>
              <select id="up-p" value={upProject} onChange={(e) => setUpProject(e.target.value)}>
                <option value="">{t('up.projectPick')}</option>
                {myProjects.length > 0 && <optgroup label={t('common.myProjects')}>{myProjects.map((p) => <option key={p.id} value={p.id}>{lookups.customers.find((c) => c.id === p.customerId)?.name} · {p.name}</option>)}</optgroup>}
                <optgroup label={t('common.all')}>{lookups.projects.filter((p) => !myProjects.includes(p)).map((p) => <option key={p.id} value={p.id}>{lookups.customers.find((c) => c.id === p.customerId)?.name} · {p.name}</option>)}</optgroup>
              </select>
              <div className="muted small">{t('up.projectHelp')}</div>
            </div>
            <label className={`drop full${dragging ? ' over' : ''}${upState ? ' busy' : ''}`}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }} onDragLeave={() => setDragging(false)}
              onDrop={(e) => { e.preventDefault(); setDragging(false); if (!upState) void uploadFiles([...e.dataTransfer.files]); }}>
              {upState ? t('up.busy', upState) : t('up.drop')}
              <input type="file" accept="application/pdf,.pdf" multiple hidden disabled={!!upState} onChange={(e) => { const fs = [...(e.target.files ?? [])]; e.target.value = ''; void uploadFiles(fs); }} />
            </label>
            {upSummary && <div role="status" className="small">{upSummary.map((l, k) => <div key={k} className={k ? 'muted' : undefined}>{l}</div>)}</div>}
          </div>
        )}
        {open && (
          <div className="wdsync-detail">
            <table><tbody>{scan.folders.map((f) => <tr key={f.projectId}><td>{f.customer} · <b>{f.project}</b></td><td className="mono small">{/^https?:\/\//.test(f.folder) ? <a href={f.folder} target="_blank" rel="noreferrer">{shortLink(f.folder)}</a> : f.folder}</td></tr>)}</tbody></table>
            {scan.status.recent.length > 1 && (<><div className="lbl">{t('sync.history')}</div><ul className="small wdsync-hist">{scan.status.recent.map((r) => <li key={r.id}><ScanRun run={r} /></li>)}</ul></>)}
          </div>
        )}
      </div>

      {!list.length && all.length > 0 && (
        <p className="muted small" style={{ margin: 0 }}>{t('inbox.others', { n: all.length })} · <button className="linkbtn" onClick={() => setShowAll(true)}>{t('inbox.showAll', { n: all.length })}</button></p>
      )}

      {list.length > 0 && (
        <div id="gefunden" ref={boxRef}>
        <Panel id="orders.inbox" className="inbox" title={t('inbox.title', { n: list.length })} hint={t('inbox.hintAll')}
          summary={<>{queueable.length > 0 && <Chip tone="info"><b>{queueable.length}</b> {t('inbox.toReview')}</Chip>}</>}
          actions={<>
            {all.length > mine.length && <button className="btn ghost" aria-pressed={showAll} onClick={() => setShowAll((v) => !v)}>{showAll ? t('inbox.showMine') : t('inbox.showAll', { n: all.length })}</button>}
            {canEdit && <button className="btn ghost" onClick={() => act('reanalyze', () => api('POST', '/api/orders/inbox/reanalyze'), t('inbox.reanalyzed'))} disabled={!!busy}>{t('inbox.reanalyze')}</button>}
            {canEdit && queueable.length > 0 && <button className="btn primary" onClick={startQueue} disabled={!!busy || !lookups}>{t('inbox.startQueue', { n: queueable.length })}</button>}
          </>}>
          <div className="tablewrap">
            <table>
              <thead><tr><th>{t('inbox.col.file')}</th><th>{t('common.project')}</th><th>{t('inbox.col.found')}</th><th className="r">{t('orders.col.volume')}</th><th>{t('common.status')}</th><th /></tr></thead>
              <tbody>
                {list.map((i) => {
                  const s = i.suggestion;
                  const total = s ? s.positions.reduce((x, p) => x + p.amountCents, 0) : 0;
                  return (
                    <tr key={i.id}>
                      <td><button className="linkbtn" onClick={() => openFile(`/api/orders/inbox/${i.id}/pdf`).catch((e: Error) => say(e.message))} title={i.filePath}>{i.fileName}</button><div className="muted small">{dfmt(i.foundAt.slice(0, 10))} · {i.uploadedBy ? t('inbox.uploadedBy', { name: i.uploadedBy.name }) : t('inbox.scanned')}</div></td>
                      <td>{i.customer.name} · {i.project.name}</td>
                      <td>
                        {s ? <><span className="mono">{s.orderNumber ?? t('common.noNumber')}</span> · {dfmt(s.orderDate)}</> : <span className="muted">–</span>}
                        {s?.docType === 'offer' && <div className="muted small">{t('inbox.offer')}</div>}
                        {s?.docType === 'changeRequest' && <div className="muted small">{t('inbox.cr', { parent: s.parentNumber ?? '–' })}</div>}
                      </td>
                      <td className="r num">{s ? eur2(total) : '–'}</td>
                      <td>
                        {i.ocr === 'pending' ? <span className="pill info">{t('inbox.ocrPending')}</span>
                          : i.ocr === 'failed' ? <span className="pill crit" title={i.error ?? ''}>{t('inbox.ocrFailed')}</span>
                          : i.error ? <span className="pill crit" title={i.error}>{t('inbox.unreadable')}</span>
                          : i.status === 'duplicate' ? <span className="pill neu">{t('inbox.exists')} <Link to={`/bestellungen/${i.existing!.id}`}>{i.existing!.orderNumber}</Link></span>
                          : s?.signed === false ? <span className="pill crit">{t('inbox.unsigned')}</span>
                          : s?.warnings.length ? <span className="pill crit">{t('inbox.check')}</span> : <span className="pill pos">{t('inbox.ready')}</span>}
                      </td>
                      <td className="r nowrap">
                        {canEdit && i.status === 'new' && i.ocr !== 'pending' && <button className="btn primary" onClick={() => { setQueue(null); void review(i); }} disabled={!!busy || !lookups || !i.canReview} title={i.canReview ? undefined : t('inbox.pmOnly')}>{i.error ? t('inbox.manual') : t('inbox.review')}</button>}
                        {canEdit && i.status === 'duplicate' && !i.existing!.hasPdf && <button className="btn" onClick={() => act(i.id, () => api('POST', `/api/orders/inbox/${i.id}/attach`), t('inbox.attached'))} disabled={!!busy || !i.canReview} title={i.canReview ? undefined : t('inbox.pmOnly')}>{t('inbox.attach')}</button>}
                        {canEdit && <button className="btn ghost" onClick={() => act(i.id, () => api('POST', `/api/orders/inbox/${i.id}/dismiss`), t('inbox.dismissed'))} disabled={!!busy || !i.canReview} title={i.canReview ? undefined : t('inbox.pmOnly')}>{t('inbox.dismiss')}</button>}
                        {canEdit && !i.canReview && <div className="muted small">{t('inbox.pmOnly')}</div>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Panel>
        </div>
      )}

      {form && lookups && (
        <OrderForm key={form.item.id} order={null} lookups={lookups} pdf={form.pdf} inboxId={form.item.id} onClose={() => { setForm(null); setQueue(null); reloadItems(); }}
          queue={queue ? {
            pos: queue.pos + 1, total: queue.ids.length,
            onSkip: () => advance(),
            onDismiss: () => { const id = form.item.id; void api('POST', `/api/orders/inbox/${id}/dismiss`).then(() => { say(t('inbox.dismissed')); advance(); }, (e: Error) => say(e.message)); },
          } : undefined}
          onSaved={(id) => { if (queue) { say(t('order.saved')); onChanged?.(); advance(); } else { setForm(null); onCreated(id); } }} />
      )}
    </>
  );
}

/** Lesbare Kurzform eines SharePoint-Links: die letzten drei Ordner. */
function shortLink(link: string): string {
  const p = link.replace(/\?.*$/, '');
  let d = p;
  try { d = decodeURIComponent(p); } catch { /* unverändert lassen */ }
  return d.split('/').slice(-3).join(' / ');
}

function ScanRun({ run }: { run: WorkdaySyncRun }) {
  const { t } = useI18n();
  const when = run.finishedAt ? dtfmt(run.finishedAt) : '–';
  const how = t(run.kind === 'auto' ? 'sync.kind.auto' : 'sync.kind.manual', { by: run.by ?? '–' });
  if (run.status === 'failed') return <div className="wdsync-fail"><b>{when}</b> · {how} · {t('sync.failed')}{run.message ? `: ${run.message}` : ''}</div>;
  const parts = [
    <span key="s">{t('scan.scanned', { n: run.scannedCount })}</span>,
    run.newCount ? <span key="n" className="wdsync-new">{t('scan.new', { n: run.newCount })}</span> : <span key="n">{t('sync.nothingNew')}</span>,
    run.differsCount ? <span key="d">{t('scan.dup', { n: run.differsCount })}</span> : null,
    run.unmatchedCount ? <span key="u" className="wdsync-warn">{t('scan.err', { n: run.unmatchedCount })}</span> : null,
  ].filter(Boolean);
  return <div><b>{when}</b> · {how}{parts.map((p, i) => <span key={i}> · {p}</span>)}{run.message && <div className="muted small">{run.message}</div>}</div>;
}
